import { useState } from 'react'

import { api } from '../api/client'
import { AuditTable, Paginazione } from '../components/AuditTable'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Audit / Log del tenant: chi è entrato e chi ha cambiato cosa.
 *
 * Due domande in una pagina — l'**accesso** (chi è entrato, con che esito) e
 * l'**operazione** (chi ha creato, modificato, cancellato) — distinte dal
 * filtro in alto. La paginazione è lato server: l'audit cresce senza limite, e
 * caricarlo tutto per ordinarlo nel browser sarebbe una pagina che con l'uso
 * diventa sempre più lenta.
 */
export default function Audit() {
  const [category, setCategory] = useState('')
  const [q, setQ] = useState('')
  const [ricerca, setRicerca] = useState('')
  const [limit, setLimit] = useState(25)
  const [offset, setOffset] = useState(0)

  const dati = useAsync(
    () => api.audit({ category: category || undefined, q: q || undefined, limit, offset }),
    [category, q, limit, offset],
  )

  function cercaOra(valore: string) {
    setQ(valore)
    setOffset(0)
  }

  if (dati.loading && !dati.data) return <Spinner />

  const d = dati.data
  const eventi = d?.events ?? []
  const totale = d?.total ?? 0

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Audit / Log')}</h1>
          <p className="page__subtitle">
            {t('Registro attività: traccia gli accessi e le operazioni eseguite nel tenant.')}
          </p>
        </div>
      </header>

      <Card title={t('Eventi')}>
        <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <label className="field__inline">
            {t('Tipo')}
            <select value={category} onChange={(e) => { setCategory(e.target.value); setOffset(0) }}>
              <option value="">{t('Tutti')}</option>
              <option value="access">{t('Accessi')}</option>
              <option value="operation">{t('Operazioni')}</option>
            </select>
          </label>
          <form
            onSubmit={(e) => { e.preventDefault(); cercaOra(ricerca) }}
            className="toolbar"
            style={{ gap: 6 }}
          >
            <input
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder={t('Cerca per email, IP, oggetto…')}
              aria-label={t('Cerca negli eventi')}
              style={{ minWidth: 220 }}
            />
            <button type="submit" className="secondary">{t('Cerca')}</button>
            {q && (
              <button type="button" className="secondary"
                      onClick={() => { setRicerca(''); cercaOra('') }}>
                {t('Azzera')}
              </button>
            )}
          </form>
          <label className="field__inline">
            {t('Per pagina')}
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0) }}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>

        <ErrorBanner error={dati.error} />

        {eventi.length === 0 ? (
          <EmptyState
            title={t('Nessun evento')}
            hint={t('Gli accessi e le operazioni compaiono qui man mano che avvengono.')}
          />
        ) : (
          <>
            <AuditTable events={eventi} />
            <Paginazione
              total={totale}
              limit={limit}
              offset={offset}
              onVai={setOffset}
            />
          </>
        )}
      </Card>
    </>
  )
}
